<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';

const props = defineProps({
    data: {
        type: Array,
        default: () => [],
    },
    dimensions: {
        type: Object,
        default: () => ({}),
    },
    dimensionX: {
        type: String,
        default: 'VI',
    },
    dimensionY: {
        type: String,
        default: 'AI',
    },
});

const emit = defineEmits(['update:dimensionX', 'update:dimensionY']);

const dimensionX = ref(props.dimensionX);
const dimensionY = ref(props.dimensionY);

watch(() => props.dimensionX, v => { if (v !== dimensionX.value) dimensionX.value = v; });
watch(() => props.dimensionY, v => { if (v !== dimensionY.value) dimensionY.value = v; });
watch(dimensionX, v => { if (v !== props.dimensionX) emit('update:dimensionX', v); });
watch(dimensionY, v => { if (v !== props.dimensionY) emit('update:dimensionY', v); });

const xLabel = computed(() => props.dimensions[dimensionX.value] || dimensionX.value);
const yLabel = computed(() => props.dimensions[dimensionY.value] || dimensionY.value);

const canvasRef = ref(null);
let chart = null;
let metaData = [];
let currentHoverUrl = null;

const colors = [
    'rgba(54, 162, 235, 0.8)',
    'rgba(255, 206, 86, 0.8)',
    'rgba(75, 192, 192, 0.8)',
    'rgba(153, 102, 255, 0.8)',
    'rgba(255, 159, 64, 0.8)',
    'rgba(255, 99, 132, 0.8)',
];

function buildDatasets() {
    const datasets = [];
    metaData = [];

    props.data.forEach((textSelection) => {
        const points = [];
        const meta = [];
        (textSelection.texts || []).forEach((text) => {
            points.push([text.values[dimensionX.value], text.values[dimensionY.value]]);
            meta.push({ url: text.text_url, title: text.title });
        });
        datasets.push({
            label: textSelection.name,
            data: points,
            backgroundColor: colors[datasets.length % colors.length],
            borderColor: colors[datasets.length % colors.length],
            borderWidth: 1,
        });
        metaData.push(meta);
    });

    return { labels: [], datasets };
}

function buildOptions() {
    return {
        plugins: {
            legend: { display: true, position: 'bottom' },
            tooltip: {
                callbacks: {
                    title(items) {
                        const item = items[0];
                        return metaData[item.datasetIndex]?.[item.dataIndex]?.title || '';
                    },
                },
            },
            zoom: {
                pan: { enabled: true, mode: 'xy' },
                zoom: {
                    wheel: { enabled: true },
                    pinch: { enabled: true },
                    mode: 'xy',
                },
                limits: {
                    x: { min: 'original', max: 'original' },
                    y: { min: 'original', max: 'original' },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: false,
                title: {
                    display: true,
                    text: yLabel.value,
                    font: { size: 20, style: 'normal' },
                    padding: { top: 0, left: 0, right: 0, bottom: 0 },
                    align: 'end',
                },
            },
            x: {
                beginAtZero: false,
                title: {
                    display: true,
                    text: xLabel.value,
                    font: { size: 20, style: 'normal' },
                    padding: { top: 0, left: 0, right: 0, bottom: 0 },
                    align: 'end',
                },
            },
        },
        onClick() {
            if (currentHoverUrl) {
                window.open(currentHoverUrl, '_blank');
            }
        },
        onHover(event) {
            const points = event.chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
            if (points.length) {
                if (!currentHoverUrl) {
                    currentHoverUrl = metaData[points[0].datasetIndex]?.[points[0].index]?.url;
                    event.chart.canvas.classList.add('cursor-pointer');
                }
            } else if (currentHoverUrl) {
                currentHoverUrl = null;
                event.chart.canvas.classList.remove('cursor-pointer');
            }
        },
    };
}

const showChart = computed(() => props.data.length > 0);

function destroyChart() {
    if (chart) {
        chart.destroy();
        chart = null;
    }
}

function render() {
    if (!showChart.value || !canvasRef.value) return;

    const chartData = buildDatasets();
    const options = buildOptions();

    if (chart) {
        chart.data = chartData;
        chart.options = options;
        chart.update();
    } else {
        const Chart = window.chartJS.Chart;
        chart = new Chart(canvasRef.value.getContext('2d'), {
            type: 'scatter',
            data: chartData,
            options,
        });
    }
}

watch(showChart, async (visible) => {
    if (!visible) {
        destroyChart();
    } else {
        await nextTick();
        render();
    }
});

watch([() => props.data, dimensionX, dimensionY], async () => {
    if (!showChart.value) {
        destroyChart();
        return;
    }
    await nextTick();
    render();
}, { deep: true });

onMounted(async () => {
    await nextTick();
    if (showChart.value) render();
});

onBeforeUnmount(() => {
    destroyChart();
});
</script>

<template>
    <div>
        <canvas v-if="showChart" ref="canvasRef" width="800" height="400"></canvas>
        <p v-else class="text-muted border rounded p-3 mb-0 mt-2">
            Keine Daten geladen. Wählen Sie Datensätze und klicken Sie auf „Daten laden“.
        </p>
        <div class="row mt-4">
            <label class="col">
                Y-Achse
                <select class="form-control" v-model="dimensionY">
                    <option v-for="(label, key) in dimensions" :key="key" :value="key">{{ label }}</option>
                </select>
            </label>
            <label class="col">
                X-Achse
                <select class="form-control" v-model="dimensionX">
                    <option v-for="(label, key) in dimensions" :key="key" :value="key">{{ label }}</option>
                </select>
            </label>
        </div>
    </div>
</template>
